// @ts-nocheck
import { Plus, Trash2, Users } from 'lucide-react';
import { workflowTableIconButtonDangerClass } from '../../../../components/WorkflowFormPrimitives';
import { isValidEmail } from '../../../../../../utils/email/isValidEmail';
import {
  DUPLICATE_SHAREHOLDER_MESSAGE,
  hasShareholderNameOrEmailConflict,
} from '../../../../../../utils/shareholder/shareholderMatchKey';

const ROW_INPUT_CLASS =
  'h-9 px-3 text-xs border border-gray-200 rounded-md outline-none focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

const RowInput = ({ className = 'flex-1', ...props }) => (
  <input {...props} className={`${className} ${ROW_INPUT_CLASS}`} />
);

/**
 * Manual single-shareholder entry panel + scrollable list of currently-added rows. Used inside the
 * setup wizard's import step. Persistence is handled by the parent — this component is presentation
 * + local form state only.
 */
export const ManualEntryRow = ({
  newRow,
  setNewRow,
  shareholders,
  locked,
  manualEntryError,
  onAdd,
  onRequestRemove,
}) => {
  const nameTrimmed = newRow.name.trim();
  const emailTrimmed = newRow.email.trim();
  const shareValue = parseInt(newRow.shares, 10);
  const hasValidShares = !isNaN(shareValue) && shareValue > 0;
  const isDuplicate =
    nameTrimmed &&
    emailTrimmed &&
    isValidEmail(emailTrimmed) &&
    hasShareholderNameOrEmailConflict(shareholders, nameTrimmed, emailTrimmed);
  const canAdd = !locked && nameTrimmed && hasValidShares && emailTrimmed && isValidEmail(emailTrimmed) && !isDuplicate;

  return (
    <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Users size={14} className="text-gray-400" />
        <span className="text-xs font-semibold text-gray-900">Manual Entry</span>
      </div>
      <div className="flex items-center space-x-2">
        <RowInput
          value={newRow.name}
          onChange={(e) => setNewRow((p) => ({ ...p, name: e.target.value }))}
          placeholder="Full name"
          disabled={locked}
        />
        <RowInput
          value={newRow.email}
          onChange={(e) => setNewRow((p) => ({ ...p, email: e.target.value }))}
          placeholder="Email"
          type="email"
          autoComplete="email"
          disabled={locked}
          aria-invalid={!!manualEntryError || (emailTrimmed.length > 0 && !isValidEmail(emailTrimmed))}
        />
        <RowInput
          className="w-28"
          value={newRow.shares}
          onChange={(e) => setNewRow((p) => ({ ...p, shares: e.target.value }))}
          placeholder="Shares"
          type="number"
          min="1"
          disabled={locked}
          aria-invalid={newRow.shares.length > 0 && !hasValidShares}
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={!canAdd}
          className="h-9 px-3.5 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
        >
          <Plus size={12} />
          <span>Add</span>
        </button>
      </div>
      {(manualEntryError || isDuplicate) && (
        <p className="mt-2 text-xs text-red-600">{manualEntryError || DUPLICATE_SHAREHOLDER_MESSAGE}</p>
      )}
      {shareholders.length > 0 && (
        <div className="mt-3 border border-gray-100 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Added Shareholders</span>
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-white text-gray-700 ring-1 ring-gray-200 tabular-nums">
              {shareholders.length}
            </span>
          </div>
          <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {shareholders.map((sh) => (
              <div key={sh.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-gray-900 truncate">{sh.name}</p>
                    {sh.excluded && (
                      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        DQI Match
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {sh.email ? (
                      isValidEmail(sh.email) ? (
                        sh.email
                      ) : (
                        <span className="text-red-500">Invalid email</span>
                      )
                    ) : (
                      <span className="text-red-400">Missing email</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-700 tabular-nums">
                    {sh.shares.toLocaleString()} shares
                  </span>
                  <button
                    type="button"
                    onClick={() => onRequestRemove({ id: sh.id, name: sh.name || 'this shareholder' })}
                    disabled={locked}
                    className={`${workflowTableIconButtonDangerClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={locked ? 'Vote Room launched — locked' : 'Remove shareholder'}
                    aria-label={`Remove shareholder ${sh.name || 'row'}`}
                  >
                    <Trash2 size={18} strokeWidth={2} aria-hidden />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
